import React, { useState, useEffect } from 'react';
import { api } from '../../context/AppContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Play, X, ChevronDown, ChevronUp, Timer, ClipboardList, Pencil, Check, TrendingUp, Award, Sparkles, Dumbbell, Flag, Ghost, Gauge, Trophy, Crosshair, Home, Building2, Info, Target, Flame, Wind, Repeat, RefreshCw, Activity, Heart, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { GLOW_FILTER, glowTooltipStyle, glowTooltipItemStyle, glowTooltipLabelStyle, themeRgb } from '../charts/GlowChartDefs';
import { InlineVideoPlayer } from '../VideoPlayer';
import YumixTip from '../YumixTip';
import { useCoachTip } from '../../constants/coachTips';
import SavedPlansArchive from './SavedPlansArchive';
import SelfPlanGenerator from './SelfPlanGenerator';
import DraftPlanEditor from './DraftPlanEditor';
import WorkoutCompletionModal from './WorkoutCompletionModal';
import WorkoutDebriefForm from './WorkoutDebriefForm';
import StreakShareButton from '../StreakShareButton';
import StreakShareModal from '../StreakShareModal';
import FeatureHint from '../FeatureHint';
import { PRChallengeCard, PRVictoryModal } from './PRChallenge';
import RestTimer from './RestTimer';
import ExerciseCompleteModal from './ExerciseCompleteModal';
import ProgressionAggressivenessPrompt from './ProgressionAggressivenessPrompt';
import AggressivenessBadge from './AggressivenessBadge';
import CycleProgressBadge from '../CycleProgressBadge';
import CycleReassessmentModal from '../CycleReassessmentModal';
import EmptyPlanState from './EmptyPlanState';
import SwapSuggestionModal from './SwapSuggestionModal';
import {
  getISOWeekKeyStandalone,
  resolveWarmup,
  resolveCooldown,
  formatDurationLabel,
} from './MyPlanTab.helpers';



export default function MyPlanTab({ language, user, onFullscreen }) {
  const rirTip = useCoachTip('workout.rir');
  const fr = language === 'fr';
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter. 405.j — 6-week cycle reassessment wizard state
  const [showCycleReassess, setShowCycleReassess] = useState(false);
  const [activeCycle, setActiveCycle] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(0);
  const [expandedDay, setExpandedDay] = useState(null);
  const [editingEx, setEditingEx] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [exHistory, setExHistory] = useState({});
  const [exVideos, setExVideos] = useState({});
  const [planVideo, setPlanVideo] = useState(null);
  const [planRestSeconds, setPlanRestSeconds] = useState(null);  // RestTimer: seconds to run when not null
  const [pendingAdvanceKey, setPendingAdvanceKey] = useState(null);  // exKey whose set counter should advance when timer starts

  // Set progress tracking — OPT-IN, local-only, per-day
  // Shape: { "wi-di-ei": completedSetsCount }
  // Users who never click the Timer or dots won't see any tracking UI activate.
  const getTodayKey = () => {
    const d = new Date();
    return `yumix_set_progress_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const getLoggedKey = () => {
    const d = new Date();
    return `yumix_ex_logged_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const getRirKey = () => {
    const d = new Date();
    return `yumix_ex_rir_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [setProgress, setSetProgress] = useState(() => {
    try { return JSON.parse(localStorage.getItem(getTodayKey()) || '{}'); } catch { return {}; }
  });
  const [loggedToday, setLoggedToday] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(getLoggedKey()) || '[]')); } catch { return new Set(); }
  });
  // RIR (Reps in Reserve) logged per exercise for today's session.
  // Stored per exKey, persisted in localStorage so reloads/navigations don't lose user input.
  const [loggedRir, setLoggedRir] = useState(() => {
    try { return JSON.parse(localStorage.getItem(getRirKey()) || '{}'); } catch { return {}; }
  });
  // Modal that explains the RIR concept (the "reservoir analogy")
  const [showRirInfo, setShowRirInfo] = useState(false);
  // Coach YUMIX auto-progression recommendations, keyed by lower-case exercise name.
  // Populated once per plan load via /api/workouts/recommendation-batch.
  const [recommendations, setRecommendations] = useState({});
  const [celebrationKey, setCelebrationKey] = useState(null);  // exKey that just completed — triggers confetti
  const [celebrationEx, setCelebrationEx] = useState(null);  // full exercise data for the Celebration modal
  const [weeklyStats, setWeeklyStats] = useState(null);  // { week_total_sets, today_total_sets, next_milestone, ... }

  // Iter. 406.fr — REMPLACER L'EXERCICE (Swap)
  // Single-exercise swap workflow. Two phases: suggest -> apply.
  // Shape: { wi, di, ei, current, suggestion, loading, applying, error, excludeNames }
  const [swapState, setSwapState] = useState(null);


  // Progression aggressiveness — user-level preference that modulates Coach YUMIX bumps.
  // Mirrored in real-time so the badge can be tweaked mid-workout and the recos update instantly.
  const [aggressiveness, setAggressiveness] = useState('standard');
  const [aggressivenessLoaded, setAggressivenessLoaded] = useState(false);

  // 🔁 ADAPTIVE LOOP — when the user logs RIR=0 on the same exercise twice
  // in a row (current session + previous saved log), the Coach YUMIX gently
  // suggests switching to "Conservateur" for the rest of the week. Shown as
  // a discrete inline banner under the exercise (NOT a blocking modal).
  // - `failureSuggestion`: { exKey, exName } of the exercise that just triggered
  // - dismissed keys are persisted in localStorage for the rest of the ISO week
  const [failureSuggestion, setFailureSuggestion] = useState(null);
  const getISOWeekKey = () => {
    const d = new Date();
    const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = (tmp.getUTCDay() + 6) % 7;
    tmp.setUTCDate(tmp.getUTCDate() - dayNum + 3);
    const firstThu = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
    const week = 1 + Math.round(((tmp - firstThu) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
    return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
  };
  const getFailureDismissKey = () => `yumix_failure_suggest_dismissed_${getISOWeekKey()}`;
  const [failureDismissed, setFailureDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`yumix_failure_suggest_dismissed_${getISOWeekKeyStandalone()}`) || '[]')); } catch { return new Set(); }
  });

  // Persist failureDismissed to localStorage
  useEffect(() => {
    try { localStorage.setItem(getFailureDismissKey(), JSON.stringify([...failureDismissed])); } catch { /* localStorage unavailable (private mode / quota) */ }
  }, [failureDismissed]);

  useEffect(() => {
    try { localStorage.setItem(getTodayKey(), JSON.stringify(setProgress)); } catch { /* localStorage unavailable */ }
  }, [setProgress]);
  useEffect(() => {
    try { localStorage.setItem(getLoggedKey(), JSON.stringify([...loggedToday])); } catch { /* localStorage unavailable */ }
  }, [loggedToday]);
  useEffect(() => {
    try { localStorage.setItem(getRirKey(), JSON.stringify(loggedRir)); } catch { /* localStorage unavailable */ }
  }, [loggedRir]);

  // Fire-and-forget log when an exercise just hit full completion
  const logCompletedExercise = async (exKey, ex) => {
    if (loggedToday.has(exKey)) return;
    if (!plan?.plan_id) return;
    const [, , ei] = exKey.split('-').map(Number);
    // Optimistic UI: mark as logged IMMEDIATELY so the confetti / badge fires
    // without waiting on the 300-500 ms server round-trip. Rollback on error.
    setLoggedToday(prev => { const n = new Set(prev); n.add(exKey); return n; });
    // Resolve RIR: user-logged value wins, otherwise fall back to coach-prescribed target_rir.
    const userRir = loggedRir[exKey];
    const fallbackRir = ex.target_rir;
    let rirToSend = null;
    if (userRir !== undefined && userRir !== null && userRir !== '') {
      const n = parseInt(userRir, 10);
      if (!Number.isNaN(n) && n >= 0 && n <= 4) rirToSend = n;
    } else if (typeof fallbackRir === 'number' && fallbackRir >= 0 && fallbackRir <= 4) {
      rirToSend = fallbackRir;
    }
    try {
      await api.post('/workouts/log-exercise', {
        plan_id: plan.plan_id,
        exercise_index: ei,
        exercise_name: ex.name || '',
        log: {
          sets: parseInt(ex.sets, 10) || 0,
          reps: ex.reps || '',
          weight: ex.weight || '',
          weight_unit: 'lbs',
          rir: rirToSend,
        },
      });
      // Refresh weekly stats so the counter + debrief tick up live
      try {
        const r = await api.get('/workouts/weekly-sets-count');
        setWeeklyStats(r.data);
      } catch { /* */ }
    } catch (e) {
      // Rollback optimistic update on server failure so the user can retry
      setLoggedToday(prev => { const n = new Set(prev); n.delete(exKey); return n; });
    }
  };

  const celebrate = (exKey, ex) => {
    setCelebrationKey(exKey);
    setCelebrationEx({ exKey, ex });
    logCompletedExercise(exKey, ex);
    setTimeout(() => setCelebrationKey(null), 1400);
  };

  const advanceSet = (exKey, totalSets, ex) => {
    setSetProgress(prev => {
      const current = prev[exKey] || 0;
      if (current >= totalSets) return prev;
      const next = current + 1;
      if (next === totalSets && ex) {
        // Just hit full completion
        setTimeout(() => celebrate(exKey, ex), 0);
      }
      return { ...prev, [exKey]: next };
    });
  };
  const toggleSetDot = (exKey, setIdx, totalSets, ex) => {
    setSetProgress(prev => {
      const current = prev[exKey] || 0;
      const next = (current === setIdx + 1) ? setIdx : Math.min(setIdx + 1, totalSets);
      if (next === totalSets && current < totalSets && ex) {
        setTimeout(() => celebrate(exKey, ex), 0);
      }
      return { ...prev, [exKey]: next };
    });
  };

  const isCoachOrAdmin = ['coach', 'super_admin', 'admin'].includes(user?.role);
  const [showGenerator, setShowGenerator] = useState(false);
  const [genForm, setGenForm] = useState({ focus: 'general', daysPerWeek: 4, weeks: 4, equipment: 'gym', notes: '', coachDirectives: '' });
  const [generating, setGenerating] = useState(false);
  const [draftPlan, setDraftPlan] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditing, setAuditing] = useState(false);

  // OPERATION SIGNAL DE FIN states
  const [completing, setCompleting] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [todayCompleted, setTodayCompleted] = useState(false);

  // OPERATION DEBRIEFING TACTIQUE states
  const [showDebrief, setShowDebrief] = useState(false);

  // OPERATION VITRINE SOCIALE states
  const [showStreakShare, setShowStreakShare] = useState(false);
  const [streakData, setStreakData] = useState({ streak: 0, gradeId: 'recrue' });

  // OPERATION FANTOME + RECORDS states
  const [ghostData, setGhostData] = useState({});
  const [myStats, setMyStats] = useState(null);

  // DEFI PERSONNEL states
  const [prChallenges, setPrChallenges] = useState([]);
  const [prVictory, setPrVictory] = useState(null);

  // MULTI-PLAN: Protocol (Plan B) states
  const [protocol, setProtocol] = useState(null);
  const [viewMode, setViewMode] = useState('main'); // 'main' or 'protocol'

  const [hasArchivedPlan, setHasArchivedPlan] = useState(false);
  const [lastPlanName, setLastPlanName] = useState('');
  
  // MODULE 1: Environment switch (gym/home)
  const [trainEnv, setTrainEnv] = useState(user?.training_environment || 'gym');
  const [switchingEnv, setSwitchingEnv] = useState(false);
  const [noEnvPlan, setNoEnvPlan] = useState(false);

  const handleEnvSwitch = async (env) => {
    if (env === trainEnv && !noEnvPlan) return;
    setSwitchingEnv(true);
    setNoEnvPlan(false);
    try {
      const res = await api.put('/training-plans/switch-environment', { environment: env });
      setTrainEnv(env);
      if (res.data.switched && res.data.has_plan) {
        // Reload active plan
        const planRes = await api.get('/training-plans/active');
        setPlan(planRes.data.plan);
        if (planRes.data.plan?.weeks?.length > 0) setExpandedWeek(0);
        toast.success(fr ? `Plan ${env === 'gym' ? 'Gym' : 'Maison'} active!` : `${env === 'gym' ? 'Gym' : 'Home'} plan activated!`);
      } else if (!res.data.has_plan) {
        setNoEnvPlan(true);
      }
    } catch {
      toast.error(fr ? 'Erreur de changement' : 'Switch error');
    } finally {
      setSwitchingEnv(false);
    }
  };

  // Tag current plan with environment
  const tagCurrentPlan = async (env) => {
    if (!plan) return;
    try {
      await api.post('/training-plans/tag-environment', { environment: env });
      toast.success(fr ? `Plan marque comme "${env === 'gym' ? 'Gym' : 'Maison'}"` : `Plan tagged as "${env}"`);
      setNoEnvPlan(false);
    } catch { /* */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/training-plans/active');
        setPlan(res.data.plan);
        if (res.data.plan?.weeks?.length > 0) setExpandedWeek(0);
        // Track if user had a plan that was archived
        if (!res.data.plan && res.data.has_archived) {
          setHasArchivedPlan(true);
          setLastPlanName(res.data.last_plan_name || '');
        }
      } catch { /* */ }
      finally { setLoading(false); }
    })();
    // Fetch protocol (Plan B)
    (async () => {
      try {
        const res = await api.get('/training-plans/active-protocol');
        setProtocol(res.data.protocol);
      } catch { /* */ }
    })();
    // Check if today's workout is already completed
    (async () => {
      try {
        const res = await api.get('/workouts/weekly-count');
        setTodayCompleted(!!res.data.today_completed);
      } catch { /* */ }
    })();
    // Fetch personal records
    (async () => {
      try {
        const res = await api.get('/workouts/my-stats');
        setMyStats(res.data);
      } catch { /* */ }
    })();
    // Fetch active PR challenges
    (async () => {
      try {
        const res = await api.get('/workouts/pr-challenges/active');
        setPrChallenges(res.data.challenges || []);
      } catch { /* */ }
    })();
    // Also load weekly sets stats for the dopamine counter
    (async () => {
      try {
        const r = await api.get('/workouts/weekly-sets-count');
        setWeeklyStats(r.data);
      } catch { /* */ }
    })();
  }, []);

  // Iter. 406.fh — Cache invalidation: any plan-activated event (from the
  // Onboarding Wizard, J42 reassess or IA Elite Admin) triggers an immediate
  // re-fetch of the active plan. Means the user never has to manually
  // refresh — the new plan appears in "Mon Plan" the moment the deploy
  // promise resolves.
  useEffect(() => {
    const handler = async () => {
      try {
        const res = await api.get('/training-plans/active');
        setPlan(res.data.plan);
        if (res.data.plan?.weeks?.length > 0) setExpandedWeek(0);
        setHasArchivedPlan(false);
      } catch { /* silent */ }
    };
    window.addEventListener('yumix:plan-activated', handler);
    return () => window.removeEventListener('yumix:plan-activated', handler);
  }, []);

  // Reusable: fetch fresh Coach YUMIX recommendations for the current plan.
  // Called once on plan load AND whenever the user negotiates a new
  // aggressiveness mode — so the targets update live, mid-workout.
  const fetchRecommendations = React.useCallback(async (planArg) => {
    const p = planArg || plan;
    if (!p?.weeks) return;
    const names = new Set();
    const targetRepsByName = {};
    p.weeks.forEach(w => w.days?.forEach(d => d.exercises?.forEach(ex => {
      if (ex.name) {
        names.add(ex.name);
        if (ex.reps) targetRepsByName[ex.name] = ex.reps;
      }
    })));
    if (names.size === 0) return;
    const recItems = [...names].map(n => ({ name: n, target_reps: targetRepsByName[n] || '' }));
    try {
      const r = await api.post('/workouts/recommendation-batch', { exercises: recItems });
      setRecommendations(r.data.recommendations || {});
    } catch { /* silent */ }
  }, [plan]);

  // Load current aggressiveness once (independent of plan)
  useEffect(() => {
    let cancelled = false;
    api.get('/workouts/aggressiveness-check')
      .then(res => {
        if (cancelled) return;
        const v = (res.data?.current_aggressiveness || 'standard').toLowerCase();
        setAggressiveness(['conservative', 'standard', 'aggressive'].includes(v) ? v : 'standard');
      })
      .catch(() => { /* keep default */ })
      .finally(() => { if (!cancelled) setAggressivenessLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // User picks a new mode from the inline badge. Persist, then refresh recos.
  const handleAggressivenessChange = async (newValue) => {
    const prev = aggressiveness;
    setAggressiveness(newValue);  // optimistic
    try {
      await api.post('/workouts/set-aggressiveness', { aggressiveness: newValue });
      await fetchRecommendations();
      const labelMap = { conservative: { fr: '🐢 Conservateur', en: '🐢 Conservative' }, standard: { fr: '⚡ Standard', en: '⚡ Standard' }, aggressive: { fr: '🔥 Agressif', en: '🔥 Aggressive' } };
      const lbl = labelMap[newValue] || labelMap.standard;
      toast.success(fr ? `Coach YUMIX → ${lbl.fr} · cibles mises à jour` : `Coach YUMIX → ${lbl.en} · targets updated`);
    } catch {
      setAggressiveness(prev);  // rollback
      toast.error(fr ? 'Erreur lors du changement de mode' : 'Failed to change mode');
    }
  };

  // 🔁 ADAPTIVE LOOP — scan: detect "2+ consecutive RIR=0" on the same exercise.
  // Trigger conditions (any of):
  //   a) Current RIR dropdown = 0 AND user has validated 2+ sets this session
  //      (i.e. 2 consecutive failed sets in the SAME workout)
  //   b) Current RIR dropdown = 0 AND last saved log was also RIR=0
  //      (across-session repeat)
  useEffect(() => {
    if (aggressiveness === 'conservative') return;
    if (!plan?.weeks) return;
    if (failureSuggestion) return; // one suggestion at a time

    for (const [exKey, rirVal] of Object.entries(loggedRir)) {
      if (String(rirVal) !== '0') continue;
      if (failureDismissed.has(exKey)) continue;
      const [wi, di, ei] = exKey.split('-').map(Number);
      const ex = plan.weeks?.[wi]?.days?.[di]?.exercises?.[ei];
      if (!ex?.name) continue;

      // Condition (a): in-session repeat — 2+ sets already done with current RIR=0
      const setsDone = setProgress[exKey] || 0;
      if (setsDone >= 2) {
        setFailureSuggestion({ exKey, exName: ex.name });
        break;
      }

      // Condition (b): cross-session repeat — last log also had RIR=0
      const recData = recommendations[ex.name.trim().toLowerCase()];
      const prevRir = recData?.last_log?.rir;
      if (prevRir === 0) {
        setFailureSuggestion({ exKey, exName: ex.name });
        break;
      }
    }
  }, [loggedRir, recommendations, plan, aggressiveness, failureDismissed, setProgress, failureSuggestion]);

  // User accepted the suggestion: switch to Conservative + dismiss
  const acceptFailureSuggestion = async () => {
    if (!failureSuggestion) return;
    const exKey = failureSuggestion.exKey;
    setFailureDismissed(prev => { const n = new Set(prev); n.add(exKey); return n; });
    setFailureSuggestion(null);
    await handleAggressivenessChange('conservative');
  };

  // User ignored the suggestion: just dismiss for the rest of the week
  const dismissFailureSuggestion = () => {
    if (!failureSuggestion) return;
    setFailureDismissed(prev => { const n = new Set(prev); n.add(failureSuggestion.exKey); return n; });
    setFailureSuggestion(null);
  };

  useEffect(() => {
    if (!plan?.weeks) return;
    const names = new Set();
    // Track reps target per exercise name (last one wins — names should be unique within a plan)
    const targetRepsByName = {};
    plan.weeks.forEach(w => w.days?.forEach(d => d.exercises?.forEach(ex => {
      if (ex.name) {
        names.add(ex.name);
        if (ex.reps) targetRepsByName[ex.name] = ex.reps;
      }
    })));
    if (names.size === 0) return;
    (async () => {
      try {
        const recItems = [...names].map(n => ({ name: n, target_reps: targetRepsByName[n] || '' }));
        const [histRes, vidRes, ghostRes, recRes] = await Promise.all([
          api.post('/training-plans/exercise-history-batch', { exercise_names: [...names] }),
          api.post('/exercises/video-lookup', { exercise_names: [...names] }),
          api.post('/workouts/ghost-data', { exercise_names: [...names] }),
          api.post('/workouts/recommendation-batch', { exercises: recItems }),
        ]);
        setExHistory(histRes.data.histories || {});
        setExVideos(vidRes.data.videos || {});
        setGhostData(ghostRes.data.ghosts || {});
        setRecommendations(recRes.data.recommendations || {});
      } catch { /* */ }
    })();
  }, [plan]);

  const startEdit = (wi, di, ei, ex) => {
    // Auto-prefill Poids & Reps with the Coach YUMIX recommendation if available,
    // so the user only has to tap "Valider" if they followed the target (or
    // tweak the values if their performance varied).
    // ⚠️ Iter. 406.fr.49 — Bug fix : quand aucune recommandation Coach n'est
    // disponible ET que le champ Poids du plan est vide, on remplit avec le
    // POIDS DE LA DERNIÈRE SÉANCE (ghost) au lieu de laisser vide. Ainsi un
    // utilisateur qui re-génère un plan retrouve automatiquement ses derniers
    // poids atteints sur chaque exercice.
    const exName = ex.name?.trim().toLowerCase();
    const recData = recommendations[exName];
    const rec = (recData && recData.available) ? recData.recommendation : null;
    const suggestedWeight = (rec && rec.suggested_weight !== null && rec.suggested_weight !== undefined)
      ? String(rec.suggested_weight)
      : '';
    const suggestedReps = (rec && rec.suggested_reps !== null && rec.suggested_reps !== undefined)
      ? String(rec.suggested_reps)
      : '';
    const ghost = ghostData[exName];
    const ghostWeight = (ghost && ghost.weight != null && ghost.weight !== '') ? String(ghost.weight) : '';
    const ghostReps = (ghost && ghost.reps != null && ghost.reps !== '') ? String(ghost.reps) : '';
    // Priority: current plan value > Coach recommendation > last session (ghost) > empty
    const weightFallback = (ex.weight != null && ex.weight !== '')
      ? String(ex.weight)
      : (suggestedWeight || ghostWeight || '');
    const repsFallback = (ex.reps != null && ex.reps !== '')
      ? String(ex.reps)
      : (suggestedReps || ghostReps || '');

    setEditingEx(`${wi}-${di}-${ei}`);
    setEditValues({
      sets: ex.sets ?? 3,
      reps: repsFallback,
      weight: weightFallback,
      rest_seconds: ex.rest_seconds ?? 60,
      target_rir: (ex.target_rir ?? '') === '' ? '' : String(ex.target_rir),
      // Internal flags so the UI can tint the prefilled fields & hide the hint
      // as soon as the user manually edits them.
      _prefilled_weight: !!(suggestedWeight || ghostWeight),
      _prefilled_reps: !!(suggestedReps || ghostReps),
      _prefilled_from_ghost: !suggestedWeight && !!ghostWeight, // display hint
      _has_rec: !!rec,
    });
  };

  const cancelEdit = () => { setEditingEx(null); setEditValues({}); };

  const saveEdit = async (wi, di, ei) => {
    setSaving(true);
    try {
      const parsedRir = editValues.target_rir === '' || editValues.target_rir == null
        ? null
        : Math.max(0, Math.min(4, parseInt(editValues.target_rir, 10) || 0));

      // Pull the recommendation context so the backend can compute acceptance
      const exNow = plan?.weeks?.[wi]?.days?.[di]?.exercises?.[ei];
      const exKey = `${wi}-${di}-${ei}`;
      const recCtx = exNow ? recommendations[exNow.name?.trim().toLowerCase()] : null;
      const rec = (recCtx && recCtx.available) ? recCtx.recommendation : null;
      const userRirRaw = loggedRir[exKey];
      const rirLogged = (userRirRaw !== undefined && userRirRaw !== '' && userRirRaw !== null)
        ? Math.max(0, Math.min(4, parseInt(userRirRaw, 10)))
        : null;

      const res = await api.put('/training-plans/active/exercise', {
        week_index: wi, day_index: di, exercise_index: ei,
        sets: parseInt(editValues.sets) || null,
        reps: String(editValues.reps || ''),
        weight: String(editValues.weight || ''),
        rest_seconds: parseInt(editValues.rest_seconds) || null,
        target_rir: parsedRir,
        suggested_weight: rec ? rec.suggested_weight : null,
        suggested_reps: rec ? rec.suggested_reps : null,
        recommendation_delta: rec ? rec.delta : null,
        rir_logged: Number.isNaN(rirLogged) ? null : rirLogged,
      });
      setPlan(prev => {
        const updated = JSON.parse(JSON.stringify(prev));
        const ex = updated.weeks[wi].days[di].exercises[ei];
        ex.sets = parseInt(editValues.sets) || ex.sets;
        ex.reps = String(editValues.reps || ex.reps);
        ex.weight = String(editValues.weight || '');
        ex.rest_seconds = parseInt(editValues.rest_seconds) || ex.rest_seconds;
        if (parsedRir !== null) ex.target_rir = parsedRir;
        else delete ex.target_rir;
        return updated;
      });
      setEditingEx(null);
      setEditValues({});

      // 🔥 DOPAMINE TOAST — fired when the saved weight beats the previous PR
      if (res.data?.is_new_pr) {
        const exDisplay = exNow?.name || '';
        const prev = res.data?.previous_pr;
        // Confetti burst — gold/orange palette to match the toast
        try {
          confetti({
            particleCount: 90,
            spread: 75,
            startVelocity: 35,
            origin: { x: 0.5, y: 0.25 },
            colors: ['#FB923C', '#FBBF24', '#FACC15', '#FFFFFF', '#F97316'],
            scalar: 1.1,
            ticks: 220,
          });
        } catch { /* canvas-confetti is non-critical */ }
        toast.custom((t) => (
          <div
            className="bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-400 text-black rounded-2xl px-4 py-3 shadow-2xl border-2 border-amber-300 max-w-sm flex items-start gap-3 animate-in zoom-in-95 fade-in slide-in-from-top-4 duration-300"
            data-testid="pr-progression-toast"
            style={{ boxShadow: '0 0 40px rgba(251, 146, 60, 0.6), 0 0 80px rgba(251, 191, 36, 0.3)' }}
            onClick={() => toast.dismiss(t)}
          >
            <div className="text-3xl animate-bounce" aria-hidden="true">🔥</div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-base uppercase tracking-tight font-black leading-tight">
                {fr ? 'Progression auto-validée !' : 'Auto-progression unlocked!'}
              </p>
              <p className="text-xs font-semibold opacity-90 mt-0.5">
                {exDisplay} — <span className="font-black">{editValues.weight} lbs</span>
                {prev ? <span className="opacity-80"> · ancien PR {prev} lbs</span> : null}
              </p>
            </div>
          </div>
        ), { duration: 5500, position: 'top-center' });
      } else {
        toast.success(fr ? 'Exercice mis a jour !' : 'Exercise updated!');
      }

      // DEFI PERSONNEL — Check if a PR challenge was beaten
      if (res.data?.pr_challenge_beaten?.beaten) {
        setPrVictory(res.data.pr_challenge_beaten);
        // Refresh challenges list
        try {
          const cr = await api.get('/workouts/pr-challenges/active');
          setPrChallenges(cr.data.challenges || []);
        } catch { /* */ }
      }

      if (editValues.weight) {
        try {
          const allNames = new Set();
          const allItems = [];
          plan.weeks.forEach(w => w.days?.forEach(d => d.exercises?.forEach(ex => {
            if (ex.name) {
              allNames.add(ex.name);
              allItems.push({ name: ex.name, target_reps: ex.reps || '' });
            }
          })));
          const [histRes, statsRes, recRes] = await Promise.all([
            api.post('/training-plans/exercise-history-batch', { exercise_names: [...allNames] }),
            api.get('/workouts/my-stats'),
            api.post('/workouts/recommendation-batch', { exercises: allItems }),
          ]);
          setExHistory(histRes.data.histories || {});
          setMyStats(statsRes.data);
          setRecommendations(recRes.data.recommendations || {});
        } catch { /* */ }
      }
    } catch {
      toast.error(fr ? 'Erreur de sauvegarde' : 'Save error');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const handleSelfGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/coach/command/armurerie/${user.user_id}/generate`, {
        focus: genForm.focus, days_per_week: genForm.daysPerWeek, duration_weeks: genForm.weeks,
        equipment: genForm.equipment, notes: genForm.notes, coach_directives: genForm.coachDirectives || '',
      });
      if (res.data.success) { setDraftPlan(res.data.draft_plan); toast.success(fr ? 'Brouillon généré !' : 'Draft generated!'); }
      else {
        // Iter. 406.fi — Generator returned success:false (post-validation
        // dropped all exercises). Show retry-able toast.
        const isEmpty = res.data?.error_code === 'empty_plan_after_validation';
        const msg = isEmpty
          ? (fr ? 'Échec de génération — réessaie ou change l\'équipement.' : 'Generation failed — retry or change equipment.')
          : (res.data.error || (fr ? 'Erreur generation' : 'Generation error'));
        toast.error(msg, {
          duration: 10000,
          action: isEmpty ? { label: fr ? 'Réessayer' : 'Retry', onClick: () => { handleSelfGenerate(); } } : undefined,
        });
      }
    } catch { toast.error(fr ? 'Erreur generation du plan' : 'Plan generation error'); }
    finally { setGenerating(false); }
  };

  const handleSelfDeploy = async () => {
    if (!draftPlan) return;
    if (!auditResult) {
      setAuditing(true);
      try {
        const res = await api.post(`/coach/command/armurerie/${user.user_id}/audit`, { plan: draftPlan });
        const result = res.data;
        setAuditResult(result);
        if (result.status === 'alert' && result.conflicts?.length > 0) {
          toast.warning(fr ? `${result.conflicts.length} conflit(s) detecte(s) !` : `${result.conflicts.length} conflict(s) detected!`);
          setAuditing(false);
          return;
        }
      } catch { toast.warning(fr ? 'Audit non disponible.' : 'Audit unavailable.'); }
      finally { setAuditing(false); }
    }
    setDeploying(true);
    try {
      await api.post(`/coach/command/armurerie/${user.user_id}/deploy`, { plan: draftPlan });
      toast.success(fr ? 'Plan deploye !' : 'Plan deployed!');
      setDraftPlan(null); setShowGenerator(false); setAuditResult(null);
      const res = await api.get('/training-plans/active');
      setPlan(res.data.plan);
    } catch (err) {
      // Iter. 406.fi — Surface atomic safety-net rejection with retry.
      const code = err?.response?.data?.detail?.error_code;
      const isEmpty = code === 'empty_plan_rejected' || code === 'empty_plan_after_validation';
      const msg = isEmpty
        ? (fr ? 'Échec de génération — ton plan actuel a été conservé. Réessaie ou ajuste ton équipement.'
              : 'Generation failed — your current plan was kept. Try again or change your equipment.')
        : (fr ? 'Erreur déploiement' : 'Deploy error');
      toast.error(msg, {
        duration: 10000,
        action: isEmpty ? { label: fr ? 'Réessayer' : 'Retry', onClick: () => { handleSelfGenerate(); } } : undefined,
      });
    } finally { setDeploying(false); }
  };

  const handleForceDeployAfterAudit = async () => {
    setAuditResult(null);
    setDeploying(true);
    try {
      await api.post(`/coach/command/armurerie/${user.user_id}/deploy`, { plan: draftPlan });
      toast.success(fr ? 'Plan deploye (audit ignore) !' : 'Plan deployed (audit overridden)!');
      setDraftPlan(null); setShowGenerator(false);
      const res = await api.get('/training-plans/active');
      setPlan(res.data.plan);
    } catch (err) {
      const code = err?.response?.data?.detail?.error_code;
      const isEmpty = code === 'empty_plan_rejected' || code === 'empty_plan_after_validation';
      const msg = isEmpty
        ? (fr ? 'Échec de génération — ton plan actuel a été conservé. Réessaie ou ajuste ton équipement.'
              : 'Generation failed — your current plan was kept. Try again or change your equipment.')
        : (fr ? 'Erreur déploiement' : 'Deploy error');
      toast.error(msg, {
        duration: 10000,
        action: isEmpty ? { label: fr ? 'Réessayer' : 'Retry', onClick: () => { handleSelfGenerate(); } } : undefined,
      });
    } finally { setDeploying(false); }
  };

  // ── Iter. 406.fr — REMPLACER L'EXERCICE (Swap) — 3 alternatives ──────────
  const openSwap = async (wi, di, ei, ex) => {
    setSwapState({
      wi, di, ei,
      current: { name: ex?.name, sets: ex?.sets, reps: ex?.reps, rest_seconds: ex?.rest_seconds },
      suggestions: [],
      selectedIdx: 0,
      loading: true,
      applying: false,
      error: null,
      excludeNames: [],
    });
    try {
      const res = await api.post(`/coach/command/armurerie/${user.user_id}/swap-exercise`, {
        week_index: wi, day_index: di, exercise_index: ei,
        plan_id: plan?.plan_id,
        exclude_names: [],
      });
      if (res.data?.success) {
        const list = Array.isArray(res.data.suggestions) && res.data.suggestions.length > 0
          ? res.data.suggestions
          : (res.data.suggestion ? [res.data.suggestion] : []);
        setSwapState(s => s ? { ...s, loading: false, suggestions: list, selectedIdx: 0 } : s);
      } else {
        setSwapState(s => s ? { ...s, loading: false, error: fr ? 'Aucune suggestion.' : 'No suggestion.' } : s);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail?.error
        || (fr ? 'Echec de la suggestion. Reessaie.' : 'Suggestion failed. Retry.');
      setSwapState(s => s ? { ...s, loading: false, error: msg } : s);
    }
  };

  const regenerateSwap = async () => {
    if (!swapState) return;
    const { wi, di, ei, suggestions, excludeNames } = swapState;
    // Add the entire current batch to the exclude list so the AI proposes 3 different ones
    const newExcludes = Array.from(new Set([
      ...excludeNames,
      ...(suggestions || []).map(s => s?.name).filter(Boolean),
    ]));
    setSwapState(s => s ? { ...s, loading: true, error: null, excludeNames: newExcludes, suggestions: [], selectedIdx: 0 } : s);
    try {
      const res = await api.post(`/coach/command/armurerie/${user.user_id}/swap-exercise`, {
        week_index: wi, day_index: di, exercise_index: ei,
        plan_id: plan?.plan_id,
        exclude_names: newExcludes,
      });
      if (res.data?.success) {
        const list = Array.isArray(res.data.suggestions) && res.data.suggestions.length > 0
          ? res.data.suggestions
          : (res.data.suggestion ? [res.data.suggestion] : []);
        setSwapState(s => s ? { ...s, loading: false, suggestions: list, selectedIdx: 0 } : s);
      } else {
        setSwapState(s => s ? { ...s, loading: false, error: fr ? 'Aucune suggestion.' : 'No suggestion.' } : s);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail?.error
        || (fr ? 'Echec de la suggestion. Reessaie.' : 'Suggestion failed. Retry.');
      setSwapState(s => s ? { ...s, loading: false, error: msg } : s);
    }
  };

  const applySwap = async () => {
    if (!swapState?.suggestions?.length) return;
    const { wi, di, ei, suggestions, selectedIdx } = swapState;
    const chosen = suggestions[selectedIdx] || suggestions[0];
    if (!chosen) return;
    setSwapState(s => s ? { ...s, applying: true, error: null } : s);
    try {
      const res = await api.post(`/coach/command/armurerie/${user.user_id}/swap-exercise/apply`, {
        week_index: wi, day_index: di, exercise_index: ei,
        plan_id: plan?.plan_id,
        new_exercise: chosen,
      });
      const newEx = res.data?.exercise || chosen;
      // Optimistically update the plan locally
      setPlan(prev => {
        if (!prev) return prev;
        const updated = JSON.parse(JSON.stringify(prev));
        if (updated?.weeks?.[wi]?.days?.[di]?.exercises?.[ei]) {
          updated.weeks[wi].days[di].exercises[ei] = newEx;
        }
        return updated;
      });
      toast.success(fr ? 'Exercice remplace !' : 'Exercise swapped!');
      setSwapState(null);
    } catch (err) {
      const msg = err?.response?.data?.detail?.error
        || (fr ? 'Echec du remplacement. Reessaie.' : 'Swap failed. Retry.');
      setSwapState(s => s ? { ...s, applying: false, error: msg } : s);
    }
  };

  const closeSwap = () => setSwapState(null);



  // OPERATION DEBRIEFING TACTIQUE — Show debrief form first
  const handleCompleteWorkout = () => {
    setShowDebrief(true);
  };

  // After debrief submitted, call completion API with debrief data
  const handleDebriefSubmit = async (debrief) => {
    setShowDebrief(false);
    setCompleting(true);
    try {
      // Get current streak BEFORE completion to detect tier change
      let prevStreak = 0;
      try {
        const sr = await api.get('/workouts/streak');
        prevStreak = sr.data.streak || 0;
      } catch { /* */ }

      const res = await api.post('/workouts/complete', {
        intensity_rating: debrief.intensity_rating,
        energy_level: debrief.energy_level,
      });
      if (res.data.success) {
        setCompletionData(res.data);
        setTodayCompleted(true);
        toast.success(fr ? 'Entraînement termine !' : 'Workout completed!');

        // Check if streak tier changed -> trigger share popup after dopamine modal closes
        const newStreak = prevStreak + 1;
        const TIER_THRESHOLDS = [5, 8]; // Brasier and Inferno
        const hitNewTier = TIER_THRESHOLDS.some(t => prevStreak < t && newStreak >= t);
        if (hitNewTier) {
          // Fetch grade for the share card
          try {
            const gr = await api.get('/leaderboard/my-stats');
            setStreakData({ streak: newStreak, gradeId: gr.data.grade?.id || 'recrue' });
          } catch {
            setStreakData({ streak: newStreak, gradeId: 'recrue' });
          }
          // Delay the share popup to show after dopamine modal is closed
          setTimeout(() => setShowStreakShare(true), 500);
        }
      }
    } catch (e) {
      toast.error(fr ? 'Erreur de completion' : 'Completion error');
    } finally {
      setCompleting(false);
    }
  };

  const updateDraftExercise = (weekIdx, dayIdx, exIdx, field, value) => {
    setDraftPlan(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.weeks[weekIdx].days[dayIdx].exercises[exIdx][field] = value;
      return updated;
    });
  };

  const removeDraftExercise = (weekIdx, dayIdx, exIdx) => {
    setDraftPlan(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.weeks[weekIdx].days[dayIdx].exercises.splice(exIdx, 1);
      return updated;
    });
  };

  const addDraftExercise = (weekIdx, dayIdx) => {
    setDraftPlan(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.weeks[weekIdx].days[dayIdx].exercises.push({
        name: fr ? 'Nouvel exercice' : 'New exercise',
        sets: 3, reps: '10-12', weight: '', rest_seconds: 60, notes: ''
      });
      return updated;
    });
  };

  const focusOptions = [
    { v: 'general', l: fr ? 'General' : 'General' },
    { v: 'upper', l: fr ? 'Haut du corps' : 'Upper body' },
    { v: 'lower', l: fr ? 'Bas du corps' : 'Lower body' },
    { v: 'push_pull_legs', l: 'Push/Pull/Legs' },
    { v: 'cardio', l: 'Cardio + HIIT' },
  ];

  const draftEditorProps = {
    fr, draftPlan, setDraftPlan, deploying, auditing, auditResult,
    onDeploy: handleSelfDeploy, onForceDeployAfterAudit: handleForceDeployAfterAudit,
    onClearAudit: () => setAuditResult(null),
    onUpdateAudit: (updated) => setAuditResult(updated),
    onCancel: () => { setDraftPlan(null); setShowGenerator(false); setAuditResult(null); },
    updateExercise: updateDraftExercise, removeExercise: removeDraftExercise, addExercise: addDraftExercise,
  };

  if (!plan) {
    return (
      <EmptyPlanState
        fr={fr}
        hasArchivedPlan={hasArchivedPlan}
        lastPlanName={lastPlanName}
        isCoachOrAdmin={isCoachOrAdmin}
        showGenerator={showGenerator}
        draftPlan={draftPlan}
        setShowGenerator={setShowGenerator}
        genForm={genForm}
        setGenForm={setGenForm}
        focusOptions={focusOptions}
        generating={generating}
        handleSelfGenerate={handleSelfGenerate}
        draftEditorProps={draftEditorProps}
      />
    );
  }

  const totalDays = plan.weeks?.reduce((sum, w) => sum + (w.days?.length || 0), 0) || 0;
  const totalExercises = plan.weeks?.reduce((sum, w) =>
    sum + (w.days?.reduce((ds, d) => ds + (d.exercises?.length || 0), 0) || 0), 0) || 0;

  const getDaysUntilEnd = () => {
    if (!plan?.deployed_at || !plan?.weeks?.length) return null;
    const deployDate = new Date(plan.deployed_at);
    const endDate = new Date(deployDate.getTime() + plan.weeks.length * 7 * 24 * 60 * 60 * 1000);
    return Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
  };
  const daysLeft = getDaysUntilEnd();
  const planExpiringSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;

  return (
    <div className="space-y-4" data-testid="my-plan-tab">
      {/* Iter. 405.j — 6-week cycle progress badge + reassessment wizard */}
      <CycleProgressBadge
        language={language}
        onReassessClick={async () => {
          try {
            const r = await api.get('/training-cycles/current');
            if (r.data?.cycle) {
              setActiveCycle(r.data.cycle);
              setShowCycleReassess(true);
            }
          } catch { /* ignore */ }
        }}
      />
      <CycleReassessmentModal
        open={showCycleReassess}
        cycle={activeCycle}
        language={language}
        onClose={() => setShowCycleReassess(false)}
        onSuccess={() => {
          setShowCycleReassess(false);
          // Refresh the active plan so the new 6-week plan appears.
          setLoading(true);
          api.get('/training-plans/active').then((r) => { setPlan(r.data?.plan || null); }).finally(() => setLoading(false));
        }}
      />

      {showGenerator && !draftPlan && (
        <SelfPlanGenerator fr={fr} genForm={genForm} setGenForm={setGenForm} focusOptions={focusOptions} generating={generating} onGenerate={handleSelfGenerate} onCancel={() => setShowGenerator(false)} />
      )}
      {draftPlan && <DraftPlanEditor {...draftEditorProps} />}

      {planExpiringSoon && !showGenerator && !draftPlan && (
        <Card className="border-amber-500/40 bg-amber-500/10" data-testid="plan-expiry-banner">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
              <Timer className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-300">
                {fr ? `Ton plan se termine dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}` : `Your plan ends in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {fr ? 'Contacte ton coach pour planifier le renouvellement.' : 'Contact your coach to plan your renewal.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!showGenerator && !draftPlan && (
        <>
        {/* MULTI-PLAN TOGGLE — Plan Principal vs Protocole (only when protocol exists) */}
        {protocol && (
          <div className="flex p-1 bg-secondary/60 rounded-lg w-full" data-testid="plan-mode-toggle">
            <button
              onClick={() => setViewMode('main')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'main'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="toggle-main-plan"
            >
              <Dumbbell className="w-3.5 h-3.5" />
              {fr ? 'Plan Principal' : 'Main Plan'}
            </button>
            <button
              onClick={() => setViewMode('protocol')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'protocol'
                  ? 'bg-pink-500 text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="toggle-protocol-plan"
            >
              <Flag className="w-3.5 h-3.5" />
              {fr ? 'Protocole Actif' : 'Active Protocol'}
            </button>
          </div>
        )}

        {/* PROTOCOL VIEW — Plan B */}
        {viewMode === 'protocol' && protocol && (
          <>
            <Card className="border-pink-500/30 bg-pink-500/5" data-testid="protocol-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-pink-400" />
                    <h3 className="font-heading text-sm uppercase tracking-tight text-pink-400">
                      {protocol.plan_name}
                    </h3>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await api.delete(`/training-plans/protocol/${protocol.plan_id}`);
                        setProtocol(null);
                        setViewMode('main');
                        toast.success(fr ? 'Protocole archive. Retour au plan principal.' : 'Protocol archived. Back to main plan.');
                      } catch { toast.error(fr ? 'Erreur' : 'Error'); }
                    }}
                    className="text-xs text-muted-foreground hover:text-pink-400 transition-colors"
                    data-testid="archive-protocol-btn"
                  >
                    {fr ? 'Archiver' : 'Archive'}
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground mb-3">
                  {fr ? 'Généré par l\'IA le' : 'AI-generated on'} {new Date(protocol.deployed_at).toLocaleDateString('fr-CA')}
                </p>
                {/* Protocol weeks/days/exercises */}
                <div className="space-y-3">
                  {protocol.weeks?.map((week, wi) => (
                    <div key={wi} className="space-y-2">
                      {week.days?.map((day, di) => (
                        <div key={di} className="p-3 rounded-lg bg-secondary/40 border border-border/30">
                          <p className="text-xs font-bold text-foreground mb-2">
                            {day.day_name}{day.focus ? ` — ${day.focus}` : ''}
                          </p>
                          <div className="space-y-1.5">
                            {day.exercises?.map((ex, ei) => (
                              <div key={ei} className="flex items-center gap-2 text-xs">
                                <span className="text-pink-400/60 shrink-0">{ei + 1}.</span>
                                <span className="text-foreground/90 flex-1">{ex.name}</span>
                                <span className="text-muted-foreground shrink-0">
                                  {ex.sets}x{ex.reps}
                                </span>
                              </div>
                            ))}
                            {(!day.exercises || day.exercises.length === 0) && (
                              <p className="text-[10px] text-muted-foreground/50 italic">
                                {fr ? 'Voir les details dans le chat IA' : 'See details in AI chat'}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                {protocol.raw_content && (
                  <details className="mt-3">
                    <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground">
                      {fr ? 'Voir le texte complet' : 'View full text'}
                    </summary>
                    <pre className="mt-2 text-[10px] text-muted-foreground/80 whitespace-pre-wrap bg-secondary/30 p-3 rounded-lg max-h-40 overflow-y-auto">
                      {protocol.raw_content}
                    </pre>
                  </details>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* MAIN PLAN VIEW — only when viewMode is 'main' */}
        {viewMode === 'main' && (
        <>
        {/* ENVIRONMENT SWITCH — Maison vs Gym (Smart Toggle) */}
        <div className="mb-4 space-y-2" data-testid="env-switch">
          <div className="flex rounded-xl border-2 border-border overflow-hidden">
            <button
              onClick={() => handleEnvSwitch('home')}
              disabled={switchingEnv}
              data-testid="env-home-btn"
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-heading uppercase tracking-wider transition-all ${
                trainEnv === 'home'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <Home className="w-4 h-4" />
              {fr ? 'Maison' : 'Home'}
            </button>
            <button
              onClick={() => handleEnvSwitch('gym')}
              disabled={switchingEnv}
              data-testid="env-gym-btn"
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-heading uppercase tracking-wider transition-all ${
                trainEnv === 'gym'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <Building2 className="w-4 h-4" />
              {fr ? 'Gym' : 'Gym'}
            </button>
          </div>
          
          {/* No plan for this environment */}
          {noEnvPlan && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 space-y-2" data-testid="no-env-plan-notice">
              <p className="text-xs text-amber-400">
                {fr
                  ? `Aucun plan "${trainEnv === 'home' ? 'Maison' : 'Gym'}" sauvegarde.`
                  : `No "${trainEnv}" plan saved.`}
              </p>
              <div className="flex gap-2">
                {plan && (
                  <button
                    onClick={() => tagCurrentPlan(trainEnv)}
                    className="flex-1 px-3 py-2 rounded-lg bg-primary/15 text-primary text-xs font-bold hover:bg-primary/25 transition-all"
                    data-testid="tag-current-plan-btn"
                  >
                    {fr ? 'Marquer le plan actuel comme ' : 'Tag current plan as '}{trainEnv === 'home' ? (fr ? 'Maison' : 'Home') : 'Gym'}
                  </button>
                )}
                <button
                  onClick={() => setShowGenerator(true)}
                  className="flex-1 px-3 py-2 rounded-lg bg-amber-500/15 text-amber-400 text-xs font-bold hover:bg-amber-500/25 transition-all"
                  data-testid="generate-env-plan-btn"
                >
                  {fr ? 'Generer un plan ' : 'Generate a '}{trainEnv === 'home' ? (fr ? 'Maison' : 'Home') : 'Gym'}{fr ? ' avec le Coach' : ' plan with Coach'}
                </button>
              </div>
            </div>
          )}
        </div>
        {/* CYCLE GAUGE — Plan Progress */}
        {(() => {
          const totalWeeks = plan.weeks?.length || 1;
          let currentWeek = 1;
          if (plan.deployed_at) {
            const deployed = new Date(plan.deployed_at);
            const now = new Date();
            const daysSince = Math.floor((now - deployed) / (1000 * 60 * 60 * 24));
            currentWeek = Math.min(Math.floor(daysSince / 7) + 1, totalWeeks);
          }
          const progress = Math.round((currentWeek / totalWeeks) * 100);
          return (
            <Card className="border-border bg-card" data-testid="cycle-gauge">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold text-foreground">
                      {fr ? 'Cycle d\'entraînement' : 'Training Cycle'}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-primary" data-testid="cycle-week-label">
                    {fr ? `Semaine ${currentWeek} sur ${totalWeeks}` : `Week ${currentWeek} of ${totalWeeks}`}
                  </span>
                </div>
                <div className="w-full h-3 bg-secondary/60 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${progress}%`,
                      background: progress >= 75 ? 'linear-gradient(90deg, hsl(var(--primary)), #22c55e)' : 'hsl(var(--primary))',
                    }}
                    data-testid="cycle-progress-bar"
                  />
                </div>
                <div className="flex justify-between mt-1.5">
                  {Array.from({ length: totalWeeks }, (_, i) => (
                    <div key={i} className={`text-[9px] font-medium ${i + 1 <= currentWeek ? 'text-primary' : 'text-muted-foreground/40'}`}>
                      S{i + 1}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* WEEKLY SETS COUNTER — live dopamine widget (visible only when user has logged anything this week) */}
        {weeklyStats && weeklyStats.week_total_sets > 0 && (
          <Card className="border-primary/20 bg-primary/5" data-testid="weekly-sets-widget">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Flag className="w-4 h-4 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {fr ? 'Cette semaine' : 'This week'}
                  </span>
                </div>
                {weeklyStats.today_total_sets > 0 && (
                  <span className="text-[10px] text-muted-foreground" data-testid="weekly-today-count">
                    {fr ? `+${weeklyStats.today_total_sets} aujourd'hui` : `+${weeklyStats.today_total_sets} today`}
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-3xl font-black text-primary tabular-nums" data-testid="weekly-total-sets">
                  {weeklyStats.week_total_sets}
                </span>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">
                  {fr ? 'series validées' : 'sets logged'}
                </span>
              </div>
              {weeklyStats.next_milestone && weeklyStats.sets_until_next_milestone > 0 && (
                <div className="mt-3" data-testid="weekly-milestone">
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                    <span>{fr ? 'Prochain palier' : 'Next milestone'}</span>
                    <span className="tabular-nums font-semibold">
                      {fr
                        ? `${weeklyStats.sets_until_next_milestone} series -> ${weeklyStats.next_milestone}`
                        : `${weeklyStats.sets_until_next_milestone} sets -> ${weeklyStats.next_milestone}`}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(100, (weeklyStats.week_total_sets / weeklyStats.next_milestone) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
              {weeklyStats.today_total_volume_lbs > 0 && (
                <p className="mt-3 text-[10px] text-muted-foreground text-right" data-testid="today-volume">
                  {fr
                    ? `Volume aujourd'hui : ${Math.round(weeklyStats.today_total_volume_lbs).toLocaleString()} lbs`
                    : `Today's volume: ${Math.round(weeklyStats.today_total_volume_lbs).toLocaleString()} lbs`}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* MES RECORDS — Personal Records Summary */}
        {myStats && myStats.total_logs > 0 && (
          <Card className="border-amber-500/20 bg-amber-500/5" data-testid="my-records">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-foreground">
                  {fr ? 'Mes Records' : 'My Records'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {/* Best Set */}
                <div className="text-center p-2 rounded-lg bg-secondary/40" data-testid="record-best-set">
                  <Award className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider mb-0.5">
                    {fr ? 'Meilleur Set' : 'Best Set'}
                  </p>
                  {myStats.best_set ? (
                    <>
                      <p className="text-lg font-black text-amber-400 leading-tight">
                        {myStats.best_set.weight}<span className="text-xs text-muted-foreground">lbs</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        x {myStats.best_set.reps} reps
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground/50">—</p>
                  )}
                </div>
                {/* Max Weight */}
                <div className="text-center p-2 rounded-lg bg-secondary/40" data-testid="record-max-weight">
                  <Dumbbell className="w-4 h-4 text-red-400 mx-auto mb-1" />
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider mb-0.5">
                    {fr ? 'Poids Max' : 'Max Weight'}
                  </p>
                  <p className="text-lg font-black text-red-400 leading-tight">
                    {myStats.max_weight}<span className="text-xs text-muted-foreground">lbs</span>
                  </p>
                  {myStats.max_weight_exercise && (
                    <p className="text-[9px] text-muted-foreground truncate" title={myStats.max_weight_exercise}>
                      {myStats.max_weight_exercise}
                    </p>
                  )}
                </div>
                {/* Total Volume */}
                <div className="text-center p-2 rounded-lg bg-secondary/40" data-testid="record-total-volume">
                  <TrendingUp className="w-4 h-4 text-green-400 mx-auto mb-1" />
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider mb-0.5">
                    {fr ? 'Volume Total' : 'Total Volume'}
                  </p>
                  <p className="text-lg font-black text-green-400 leading-tight">
                    {myStats.total_volume >= 1000 ? `${(myStats.total_volume / 1000).toFixed(1)}k` : myStats.total_volume}
                  </p>
                  <p className="text-[9px] text-muted-foreground">lbs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* DEFI PERSONNEL — PR Challenges */}
        <PRChallengeCard
          challenges={prChallenges}
          onRefresh={async () => {
            try {
              const res = await api.get('/workouts/pr-challenges/active');
              setPrChallenges(res.data.challenges || []);
            } catch { /* */ }
          }}
          exerciseNames={(() => {
            const names = new Set();
            plan.weeks?.forEach(w => w.days?.forEach(d => d.exercises?.forEach(ex => {
              if (ex.name) names.add(ex.name);
            })));
            return [...names];
          })()}
          language={language}
        />

        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-base sm:text-lg font-bold uppercase tracking-tight text-foreground leading-tight" data-testid="plan-name">
                  {plan.plan_name}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {fr ? 'Par' : 'By'} {plan.coach_name || (fr ? 'Coach' : 'Coach')}
                  {(() => {
                    // Iter. 406.fi — guard against missing/invalid deployed_at
                    // (was producing "Invalid Date" on legacy plans). Show the
                    // label only when we have a real ISO date.
                    const raw = plan.deployed_at || plan.created_at || plan.start_date;
                    if (!raw) return null;
                    const d = new Date(raw);
                    if (Number.isNaN(d.getTime())) return null;
                    return <> — {fr ? 'Deploye le' : 'Deployed'} {d.toLocaleDateString('fr-CA')}</>;
                  })()}
                  {daysLeft !== null && daysLeft > 0 && (
                    <span className={`ml-2 ${planExpiringSoon ? 'text-amber-400' : 'text-muted-foreground/50'}`}>
                      ({daysLeft}j {fr ? 'restants' : 'left'})
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {isCoachOrAdmin && (
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowGenerator(true)} data-testid="regenerate-plan-btn">
                    <Sparkles className="w-3 h-3 mr-1" /> {fr ? 'Nouveau plan' : 'New plan'}
                  </Button>
                )}
                <div className="flex gap-3 text-center">
                  <div>
                    <p className="font-heading text-xl font-black text-primary">{plan.weeks?.length || 0}</p>
                    <p className="text-[9px] text-muted-foreground uppercase">{fr ? 'sem.' : 'wks'}</p>
                  </div>
                  <div>
                    <p className="font-heading text-xl font-black text-foreground">{totalDays}</p>
                    <p className="text-[9px] text-muted-foreground uppercase">{fr ? 'séances' : 'sessions'}</p>
                  </div>
                  <div>
                    <p className="font-heading text-xl font-black text-foreground">{totalExercises}</p>
                    <p className="text-[9px] text-muted-foreground uppercase">{fr ? 'exercices' : 'exercises'}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Saved Plans Archive — "Le Coffre-Fort" */}
        <SavedPlansArchive
          userId={user?.user_id}
          isCoachOrAdmin={isCoachOrAdmin}
          language={language}
          onPlanRestored={async () => {
            const res = await api.get('/training-plans/active');
            setPlan(res.data.plan);
          }}
        />

        {plan.weeks?.map((week, wi) => {
          const isExpanded = expandedWeek === wi;
          return (
            <Card key={wi} className="border-border bg-card overflow-hidden" data-testid={`plan-week-${wi}`}>
              <button onClick={() => setExpandedWeek(isExpanded ? null : wi)} className="w-full p-3 flex items-center justify-between hover:bg-secondary/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">{week.week || wi + 1}</div>
                  <h4 className="text-sm font-semibold text-foreground">{fr ? `Semaine ${week.week || wi + 1}` : `Week ${week.week || wi + 1}`}</h4>
                  <span className="text-[10px] text-muted-foreground">{week.days?.length || 0} {fr ? 'jours' : 'days'}</span>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>

              {isExpanded && (
                <div className="border-t border-border divide-y divide-border/50">
                  {week.days?.map((day, di) => {
                    const dayKey = `${wi}-${di}`;
                    const isDayExpanded = expandedDay === dayKey;
                    return (
                      <div key={di}>
                        <button onClick={() => setExpandedDay(isDayExpanded ? null : dayKey)} className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-secondary/20 transition-colors" data-testid={`plan-day-${wi}-${di}`}>
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded bg-secondary text-[10px] font-bold flex items-center justify-center text-muted-foreground">{day.day || di + 1}</span>
                            <span className="text-sm font-medium text-foreground">{day.name || `Jour ${day.day || di + 1}`}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground">{day.exercises?.length || 0} ex.</span>
                            {isDayExpanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                          </div>
                        </button>

                        {isDayExpanded && (
                          <div className="px-4 pb-3 space-y-1.5">
                            {/* 🎯 Coach YUMIX negotiation row — always visible during the workout so the
                                user can switch progression mode on the fly without leaving the screen. */}
                            {aggressivenessLoaded && (
                              <div
                                className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-secondary/40 border border-amber-500/15"
                                data-testid={`coach-mode-row-${wi}-${di}`}
                              >
                                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                                  <Sparkles className="w-3 h-3 text-amber-400" />
                                  <span>{fr ? 'Mode Coach YUMIX' : 'Coach YUMIX Mode'}</span>
                                </div>
                                <AggressivenessBadge
                                  value={aggressiveness}
                                  onChange={handleAggressivenessChange}
                                  fr={fr}
                                  testKey={`day-${wi}-${di}`}
                                />
                              </div>
                            )}
                            {/* Iter. 406.fq + 406.fr.2 — Warmup block with fallback for legacy plans */}
                            {(() => {
                              const { items: warmupItems, isDefault: warmupDefault } = resolveWarmup(day, fr);
                              const totalSec = warmupItems.reduce((s, it) => s + (Number(it.duration_seconds) || 0), 0);
                              const totalLabel = totalSec > 0 ? `~${Math.max(5, Math.round(totalSec / 60))} min` : '~5-10 min';
                              return (
                                <details
                                  open
                                  className="rounded-lg border border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-amber-500/5 mb-2 shadow-sm"
                                  data-testid={`warmup-${wi}-${di}`}
                                >
                                  <summary className="cursor-pointer list-none px-3 py-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-orange-300 hover:bg-orange-500/15 rounded-lg transition-colors">
                                    <span className="text-base" aria-hidden="true">🔥</span>
                                    <Flame className="w-3.5 h-3.5" />
                                    <span className="truncate">{fr ? 'Échauffement & Activation' : 'Warm-up & Activation'}</span>
                                    <span className="ml-auto text-[10px] font-normal text-orange-300/80 shrink-0">{totalLabel}</span>
                                    <ChevronDown className="w-3.5 h-3.5 text-orange-300/60 transition-transform shrink-0" />
                                  </summary>
                                  {warmupDefault && (
                                    <p className="px-3 pt-1 pb-1 text-[10px] text-orange-200/70 italic leading-snug" data-testid={`warmup-default-note-${wi}-${di}`}>
                                      {fr
                                        ? 'Modèle standard — régénère ton plan pour un échauffement personnalisé par l\'IA.'
                                        : 'Default template — regenerate your plan for a fully AI-personalized warm-up.'}
                                    </p>
                                  )}
                                  <ul className="px-3 pb-3 pt-1 space-y-2">
                                    {warmupItems.map((w, wIdx) => {
                                      // Iter. 406.fr.18 — Honor the backend
                                      // `is_cardio` flag so AI-injected cardio
                                      // segments (no video, text-only) get
                                      // the running-style icon by default.
                                      const inferredCardio = w.is_cardio === true || /cardio|marche|jog|v[eé]lo|rameur|elliptique|course|saut|skip|corde/i.test(w.name || '');
                                      const kind = w.kind || (inferredCardio ? 'cardio' : 'mobility');
                                      const KindIcon = kind === 'cardio' ? Activity : kind === 'breath' ? Heart : Sparkles;
                                      const durLabel = formatDurationLabel(Number(w.duration_seconds));
                                      return (
                                        <li
                                          key={wIdx}
                                          className="flex items-start gap-2.5 text-xs text-foreground/95"
                                          data-testid={`warmup-item-${wi}-${di}-${wIdx}`}
                                        >
                                          <span className="w-7 h-7 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center shrink-0 mt-0.5">
                                            <KindIcon className="w-3.5 h-3.5 text-orange-300" />
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-1.5 flex-wrap">
                                              <span className="font-semibold leading-snug">{w.name || '—'}</span>
                                              {durLabel && (
                                                <span className="text-[10px] font-bold text-orange-300 bg-orange-500/10 px-1.5 py-0.5 rounded-full">
                                                  {durLabel}
                                                </span>
                                              )}
                                            </div>
                                            {w.notes && (
                                              <p className="text-[10.5px] text-muted-foreground/85 mt-0.5 italic leading-snug">{w.notes}</p>
                                            )}
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </details>
                              );
                            })()}
                            <div className="hidden sm:flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-bold">
                              <span className="w-6 shrink-0"></span>
                              <span className="flex-1">{fr ? 'Exercice' : 'Exercise'}</span>
                              <span className="w-14 text-center">{fr ? 'Séries' : 'Sets'}</span>
                              <span className="w-14 text-center">Reps</span>
                              <span className="w-14 text-center">{fr ? 'Poids' : 'Weight'}</span>
                              <span className="w-14 text-center inline-flex items-center justify-center gap-1">
                                RIR
                                {rirTip && (
                                  <YumixTip
                                    id={rirTip.id}
                                    language={rirTip.language}
                                    size="sm"
                                    side="top"
                                    title={rirTip.title}
                                    text={rirTip.text}
                                  />
                                )}
                              </span>
                              <span className="w-14 text-center">{fr ? 'Repos' : 'Rest'}</span>
                              <span className="w-8 shrink-0"></span>
                            </div>
                            {day.exercises?.map((ex, ei) => {
                              const exKey = `${wi}-${di}-${ei}`;
                              const isEditing = editingEx === exKey;
                              const hist = exHistory[ex.name?.trim().toLowerCase()];
                              const hasHistory = hist && hist.total_logs >= 2;
                              const hasGhost = !!ghostData[ex.name?.trim().toLowerCase()];
                              const hasBottom = hasHistory || hasGhost;
                              const isPR = hist && ex.weight && parseFloat(ex.weight) >= hist.max_weight && hist.max_weight > 0;
                              const vid = exVideos[ex.name];
                              const hasVid = !!(vid?.video_url || vid?.external_video_url);
                              const isShowingVideo = planVideo?.name === ex.name;
                              const activePrChallenge = prChallenges.find(c => c.exercise_name === ex.name);
                              
                              // Environment filter: dim exercises that need gym equipment when at home
                              const gymOnlyEquip = ['barbell', 'cable', 'machine', 'smith_machine', 'lat_pulldown', 'leg_press'];
                              const exEquip = (ex.equipment || '').toLowerCase();
                              const needsGym = gymOnlyEquip.some(g => exEquip.includes(g));
                              const envMismatch = trainEnv === 'home' && needsGym;

                              return (
                                <div key={ei} className={`space-y-0 ${envMismatch ? 'opacity-40' : ''}`} data-testid={`plan-ex-${wi}-${di}-${ei}`}>
                                  {envMismatch && (
                                    <div className="px-3 py-0.5 text-[9px] text-amber-400 flex items-center gap-1">
                                      <Building2 className="w-3 h-3" /> {fr ? 'Équipement gym requis' : 'Gym equipment needed'}
                                    </div>
                                  )}
                                  {isShowingVideo && (
                                    <div className="rounded-t-lg overflow-hidden">
                                      <InlineVideoPlayer videoUrl={vid?.video_url} externalVideoUrl={vid?.external_video_url} thumbnailUrl={vid?.thumbnail_url} name={ex.name} onClose={() => setPlanVideo(null)} onFullscreen={(v) => { if (onFullscreen) onFullscreen(v); }} autoPlay />
                                    </div>
                                  )}
                                  <div className={`py-2 px-3 rounded-lg transition-colors ${isEditing ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-secondary/30'} ${(hasBottom || isShowingVideo) ? 'rounded-t-none' : ''} ${hasBottom ? 'rounded-b-none' : ''}`}>
                                    {/* Row 1: Number + Name + badges */}
                                    <div className="flex items-start gap-2">
                                      <div className="w-6 h-6 rounded bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{ei + 1}</div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground leading-snug" data-testid={`ex-name-${exKey}`}>{ex.name}</p>
                                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                          {hasVid && !isShowingVideo && (
                                            <button onClick={() => setPlanVideo({ videoUrl: vid.video_url, externalVideoUrl: vid.external_video_url, name: ex.name })} className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[9px] font-medium hover:bg-primary/20 transition-colors" data-testid={`demo-btn-${exKey}`}>
                                              <Play className="w-2.5 h-2.5" /> Demo
                                            </button>
                                          )}
                                          {isPR && (
                                            <span className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[9px] font-bold uppercase" data-testid={`pr-badge-${exKey}`}>
                                              <Award className="w-3 h-3" /> PR
                                            </span>
                                          )}
                                          {activePrChallenge && (
                                            <span className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-[9px] font-bold uppercase animate-pulse" data-testid={`pr-target-${exKey}`}>
                                              <Crosshair className="w-3 h-3" /> {activePrChallenge.target_weight}lbs
                                            </span>
                                          )}
                                        </div>
                                        {ex.notes && <p className="text-[10px] text-muted-foreground/70 mt-0.5 leading-snug">{ex.notes}</p>}
                                        {/* 🎯 Coach YUMIX recommendation based on last RIR — discreet inline subtitle */}
                                        {(() => {
                                          const recData = recommendations[ex.name?.trim().toLowerCase()];
                                          if (!recData?.available || !recData.recommendation) return null;
                                          const r = recData.recommendation;
                                          if (r.delta === 'maintain' && !recData?.last_log?.rir) return null;
                                          const unit = recData.last_log?.weight_unit || 'lbs';
                                          const w = r.suggested_weight;
                                          const reps = r.suggested_reps;
                                          const deltaColor =
                                            r.delta === 'increase_weight' ? 'text-emerald-400'
                                            : r.delta === 'increase_reps' ? 'text-primary'
                                            : r.delta === 'decrease_weight' ? 'text-amber-400'
                                            : 'text-muted-foreground';
                                          return (
                                            <p
                                              className="text-[10px] mt-1 flex items-center gap-1 leading-snug"
                                              data-testid={`reco-${exKey}`}
                                              title={fr ? r.reason_fr : r.reason_en}
                                            >
                                              <Target className={`w-3 h-3 shrink-0 ${deltaColor}`} />
                                              <span className="text-muted-foreground">
                                                {fr ? 'Cible coach' : 'Coach target'}:
                                              </span>
                                              <span className={`font-semibold ${deltaColor}`}>
                                                {w ? `${w}${unit}` : '—'}{reps ? ` × ${reps}` : ''}
                                              </span>
                                              <span className="text-muted-foreground/60 text-[9px]">
                                                · {fr ? r.reason_fr : r.reason_en}
                                              </span>
                                            </p>
                                          );
                                        })()}
                                      </div>
                                      {!isEditing && (
                                        <div className="flex items-center gap-0.5 shrink-0 sm:hidden">
                                          <button onClick={() => openSwap(wi, di, ei, ex)} className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors" title={fr ? 'Remplacer' : 'Swap'} data-testid={`swap-ex-mobile-${exKey}`}>
                                            <Repeat className="w-3.5 h-3.5" />
                                          </button>
                                          <button onClick={() => startEdit(wi, di, ei, ex)} className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title={fr ? 'Modifier' : 'Edit'} data-testid={`edit-ex-mobile-${exKey}`}>
                                            <Pencil className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* Row 2: Values (sets, reps, weight, rir, rest) */}
                                    {isEditing ? (
                                      <div className="mt-2 ml-8 space-y-1">
                                        {/* Auto-prefill hint — only shown when Coach YUMIX pre-filled at least one field */}
                                        {(editValues._prefilled_weight || editValues._prefilled_reps) && (
                                          <p
                                            className="text-[9px] text-primary/80 flex items-center gap-1 font-medium"
                                            data-testid={`prefill-hint-${exKey}`}
                                          >
                                            <Target className="w-2.5 h-2.5" />
                                            {fr
                                              ? 'Cible coach pre-remplie — ajuste si besoin puis valide'
                                              : 'Coach target pre-filled — adjust if needed and save'}
                                          </p>
                                        )}
                                        <div className="flex items-center gap-1.5">
                                          <div className="flex-1 grid grid-cols-5 gap-1.5">
                                            <div>
                                              <span className="text-[8px] uppercase text-muted-foreground/50 block mb-0.5 sm:hidden">{fr ? 'Séries' : 'Sets'}</span>
                                              <input type="number" value={editValues.sets} onFocus={(e) => { e.target.select(); if (e.target.value === '0') setEditValues(v => ({...v, sets: ''})); }} onBlur={(e) => { if (e.target.value === '') setEditValues(v => ({...v, sets: '0'})); }} onChange={e => setEditValues(v => ({...v, sets: e.target.value}))} className="w-full h-7 bg-background border border-primary/40 rounded px-1 text-xs text-center font-medium" data-testid={`edit-sets-${exKey}`} />
                                            </div>
                                            <div>
                                              <span className="text-[8px] uppercase text-muted-foreground/50 block mb-0.5 sm:hidden">Reps</span>
                                              <input
                                                value={editValues.reps}
                                                onChange={e => setEditValues(v => ({...v, reps: e.target.value, _prefilled_reps: false}))}
                                                className={`w-full h-7 bg-background rounded px-1 text-xs text-center font-medium ${
                                                  editValues._prefilled_reps
                                                    ? 'border border-primary text-primary/90 placeholder:text-primary/40'
                                                    : 'border border-primary/40'
                                                }`}
                                                data-testid={`edit-reps-${exKey}`}
                                              />
                                            </div>
                                            <div>
                                              <span className="text-[8px] uppercase text-muted-foreground/50 block mb-0.5 sm:hidden">{fr ? 'Poids' : 'Weight'}</span>
                                              <input
                                                value={editValues.weight}
                                                onChange={e => setEditValues(v => ({...v, weight: e.target.value, _prefilled_weight: false}))}
                                                className={`w-full h-7 bg-background rounded px-1 text-xs text-center font-medium ${
                                                  editValues._prefilled_weight
                                                    ? 'border border-primary text-primary/90 placeholder:text-primary/40'
                                                    : 'border border-primary/40'
                                                }`}
                                                placeholder="lbs"
                                                data-testid={`edit-weight-${exKey}`}
                                              />
                                            </div>
                                            <div>
                                              <span className="text-[8px] uppercase text-muted-foreground/50 block mb-0.5 sm:hidden">RIR</span>
                                              <select
                                                value={editValues.target_rir ?? ''}
                                                onChange={e => setEditValues(v => ({...v, target_rir: e.target.value}))}
                                                className="w-full h-7 bg-background border border-primary/40 rounded px-0.5 text-xs text-center font-medium cursor-pointer"
                                                data-testid={`edit-target-rir-${exKey}`}
                                                title={fr ? 'RIR cible (Reps en Reserve)' : 'Target RIR (Reps in Reserve)'}
                                              >
                                                <option value="">—</option>
                                                <option value="0">0</option>
                                                <option value="1">1</option>
                                                <option value="2">2</option>
                                                <option value="3">3</option>
                                                <option value="4">4</option>
                                              </select>
                                            </div>
                                            <div>
                                              <span className="text-[8px] uppercase text-muted-foreground/50 block mb-0.5 sm:hidden">{fr ? 'Repos' : 'Rest'}</span>
                                              <input type="number" value={editValues.rest_seconds} onFocus={(e) => { e.target.select(); if (e.target.value === '0') setEditValues(v => ({...v, rest_seconds: ''})); }} onBlur={(e) => { if (e.target.value === '') setEditValues(v => ({...v, rest_seconds: '0'})); }} onChange={e => setEditValues(v => ({...v, rest_seconds: e.target.value}))} className="w-full h-7 bg-background border border-primary/40 rounded px-1 text-xs text-center font-medium" data-testid={`edit-rest-${exKey}`} />
                                            </div>
                                          </div>
                                          <div className="flex gap-1 shrink-0">
                                            <button onClick={() => saveEdit(wi, di, ei)} disabled={saving} className="w-7 h-7 rounded bg-green-600/90 text-white flex items-center justify-center hover:bg-green-500 transition-colors" data-testid={`save-ex-${exKey}`} title={fr ? 'Valider' : 'Save'}>
                                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                            </button>
                                            <button onClick={cancelEdit} className="w-7 h-7 rounded bg-secondary text-muted-foreground flex items-center justify-center hover:bg-destructive/20 hover:text-destructive transition-colors" data-testid={`cancel-ex-${exKey}`}>
                                              <X className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center mt-2 ml-2">
                                        <div className="flex-1 grid grid-cols-5 gap-1.5">
                                          <div className="text-center">
                                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 block sm:hidden font-semibold">{fr ? 'Séries' : 'Sets'}</span>
                                            <span className="text-base text-foreground font-bold" data-testid={`val-sets-${exKey}`}>{ex.sets}</span>
                                          </div>
                                          <div className="text-center">
                                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 block sm:hidden font-semibold">Reps</span>
                                            <span className="text-base text-foreground font-bold" data-testid={`val-reps-${exKey}`}>{ex.reps}</span>
                                          </div>
                                          <div className="text-center">
                                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 block sm:hidden font-semibold">{fr ? 'Poids' : 'Weight'}</span>
                                            <span className="text-base font-bold" data-testid={`val-weight-${exKey}`}>
                                              {ex.weight ? <span className="text-amber-400">{ex.weight}<span className="text-[10px] text-muted-foreground ml-0.5 font-medium">lbs</span></span> : <span className="text-muted-foreground/40">-</span>}
                                            </span>
                                          </div>
                                          <div className="text-center">
                                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 block sm:hidden font-semibold">RIR</span>
                                            <span className="text-base font-bold" data-testid={`val-target-rir-${exKey}`}>
                                              {typeof ex.target_rir === 'number'
                                                ? <span className="text-primary">{ex.target_rir}</span>
                                                : <span className="text-muted-foreground/40">-</span>}
                                            </span>
                                          </div>
                                          <div className="text-center">
                                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 block sm:hidden font-semibold">{fr ? 'Repos' : 'Rest'}</span>
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setPendingAdvanceKey(exKey);
                                                setPlanRestSeconds(Number(ex.rest_seconds) || 60);
                                              }}
                                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors text-sm font-bold ${
                                                ex.rest_seconds
                                                  ? 'bg-primary/10 text-primary hover:bg-primary/20'
                                                  : 'bg-secondary text-muted-foreground hover:bg-primary/10 hover:text-primary'
                                              }`}
                                              title={fr ? 'Démarrer le chrono de repos' : 'Start rest timer'}
                                              data-testid={`start-rest-${exKey}`}
                                            >
                                              <Timer className="w-3.5 h-3.5" />
                                              <span>{ex.rest_seconds ? `${ex.rest_seconds}s` : '60s'}</span>
                                            </button>
                                          </div>
                                        </div>
                                        <div className="hidden sm:flex items-center gap-0.5 shrink-0">
                                          <button onClick={() => openSwap(wi, di, ei, ex)} className="w-7 h-7 rounded items-center justify-center text-muted-foreground hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors flex" title={fr ? 'Remplacer cet exercice' : 'Swap exercise'} data-testid={`swap-ex-${exKey}`}>
                                            <Repeat className="w-3.5 h-3.5" />
                                          </button>
                                          <button onClick={() => startEdit(wi, di, ei, ex)} className="w-7 h-7 rounded items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors flex" title={fr ? 'Modifier' : 'Edit'} data-testid={`edit-ex-${exKey}`}>
                                            <Pencil className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {/* Set progress dots — OPT-IN. Hidden until user taps Timer or a dot. Zero impact on non-users. */}
                                    {(() => {
                                      const totalSets = parseInt(ex.sets, 10) || 0;
                                      const done = setProgress[exKey] || 0;
                                      if (totalSets <= 0) return null;
                                      const showTracker = done > 0 || pendingAdvanceKey === exKey;
                                      return (
                                        <div className="flex items-center gap-2 mt-2 ml-2" data-testid={`set-dots-${exKey}`}>
                                          <div className="flex gap-1.5">
                                            {Array.from({ length: totalSets }, (_, i) => {
                                              const filled = i < done;
                                              const dotBtn = (
                                                <button
                                                  key={i}
                                                  type="button"
                                                  onClick={(e) => { e.stopPropagation(); toggleSetDot(exKey, i, totalSets, ex); }}
                                                  aria-label={`Set ${i + 1}`}
                                                  title={`Set ${i + 1}`}
                                                  data-testid={`set-dot-${exKey}-${i}`}
                                                  style={{ touchAction: 'manipulation' }}
                                                  className={`relative w-9 h-9 sm:w-8 sm:h-8 rounded-full transition-all flex items-center justify-center active:scale-90 ${
                                                    filled
                                                      ? 'bg-primary shadow-[0_0_12px_rgba(var(--primary-rgb),0.6)]'
                                                      : 'bg-secondary border-2 border-border/60 hover:border-primary/70'
                                                  }`}
                                                >
                                                  <span className={`text-[11px] font-bold ${filled ? 'text-primary-foreground' : 'text-muted-foreground/60'}`}>
                                                    {i + 1}
                                                  </span>
                                                </button>
                                              );
                                              // Show the one-time "validate set" hint on the very first set
                                              // of the very first exercise (first client workout ever).
                                              if (wi === 0 && di === 0 && ei === 0 && i === 0) {
                                                return (
                                                  <FeatureHint
                                                    key={i}
                                                    id="workout-validate-set"
                                                    title={fr ? 'Valider tes séries' : 'Log your sets'}
                                                    body={fr
                                                      ? 'Clique ici pour valider chaque série ➡️, OU lance le Timer ⏱️ pour une validation automatique à la fin.'
                                                      : 'Click here to log each set ➡️, OR launch the Timer ⏱️ for auto-validation at the end.'}
                                                    side="top"
                                                    align="start"
                                                  >
                                                    {dotBtn}
                                                  </FeatureHint>
                                                );
                                              }
                                              return dotBtn;
                                            })}
                                          </div>
                                          {showTracker && (
                                            <span className="text-[10px] text-muted-foreground tabular-nums" data-testid={`set-counter-${exKey}`}>
                                              {done}/{totalSets}
                                            </span>
                                          )}
                                          {done > 0 && done >= totalSets && (
                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${celebrationKey === exKey ? 'text-primary animate-bounce' : 'text-primary'}`}>
                                              ✓ {fr ? 'Termine' : 'Done'}
                                            </span>
                                          )}
                                          {celebrationKey === exKey && (
                                            <span className="text-base animate-ping" aria-hidden="true">🎯</span>
                                          )}
                                          {/* RIR (Reps in Reserve) — native compact select, YUMIX scale 0..4.
                                              Shows once the user starts logging sets (showTracker) to keep the
                                              row clean for users who don't care about RIR. */}
                                          {showTracker && (
                                            <label
                                              className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground"
                                              data-testid={`rir-control-${exKey}`}
                                            >
                                              <span className="uppercase tracking-wider font-semibold">RIR</span>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setShowRirInfo(true);
                                                }}
                                                className="w-3.5 h-3.5 rounded-full bg-secondary/60 hover:bg-primary/30 flex items-center justify-center transition-colors"
                                                aria-label={fr ? 'En savoir plus sur le RIR' : 'Learn about RIR'}
                                                data-testid={`rir-info-${exKey}`}
                                                title={fr ? 'Reps en Reserve — clique pour comprendre' : 'Reps in Reserve — click to learn'}
                                              >
                                                <Info className="w-2.5 h-2.5 text-muted-foreground" />
                                              </button>
                                              <select
                                                value={loggedRir[exKey] ?? ''}
                                                onChange={(e) => {
                                                  const v = e.target.value;
                                                  setLoggedRir(prev => {
                                                    const next = { ...prev };
                                                    if (v === '') delete next[exKey];
                                                    else next[exKey] = v;
                                                    return next;
                                                  });
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                className="h-9 min-w-[56px] bg-background border border-border/60 rounded-md px-2 text-base font-bold text-foreground focus:border-primary focus:outline-none cursor-pointer"
                                                style={{ touchAction: 'manipulation' }}
                                                data-testid={`rir-select-${exKey}`}
                                                title={fr ? '0=Echec / 1=Limite / 2=Lourd / 3=Modere / 4=Leger' : '0=Failure / 1=Limit / 2=Hard / 3=Moderate / 4=Light'}
                                                aria-label={fr ? `RIR pour ${ex.name}` : `RIR for ${ex.name}`}
                                              >
                                                <option value="">—</option>
                                                <option value="0">0</option>
                                                <option value="1">1</option>
                                                <option value="2">2</option>
                                                <option value="3">3</option>
                                                <option value="4">4</option>
                                              </select>
                                              {typeof ex.target_rir === 'number' && (
                                                <span
                                                  className="text-[9px] text-muted-foreground/60"
                                                  title={fr ? 'Cible coach' : 'Coach target'}
                                                >
                                                  /{ex.target_rir}
                                                </span>
                                              )}
                                            </label>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  {hasHistory && !isEditing && (
                                    <div className={`flex items-center gap-2 px-3 py-1.5 bg-secondary/20 border-t border-border/30 ${hasGhost ? '' : 'rounded-b-lg'}`} data-testid={`sparkline-${exKey}`}>
                                      <TrendingUp className="w-3 h-3 text-primary/50 shrink-0" />
                                      <div className="w-20 h-6 shrink-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                          <LineChart data={hist.timeline}>
                                            <defs>
                                              <linearGradient id={`plan-spark-${exKey}`} x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#67e8f9" />
                                                <stop offset="100%" stopColor="#22d3ee" />
                                              </linearGradient>
                                            </defs>
                                            <Line
                                              type="monotone"
                                              dataKey="weight"
                                              stroke={`url(#plan-spark-${exKey})`}
                                              strokeWidth={2}
                                              dot={false}
                                              style={{ filter: GLOW_FILTER(themeRgb('cyan')) }}
                                            />
                                            <Tooltip
                                              cursor={{ stroke: `rgba(${themeRgb('cyan')},0.30)`, strokeWidth: 1 }}
                                              contentStyle={glowTooltipStyle(themeRgb('cyan'))}
                                              itemStyle={glowTooltipItemStyle()}
                                              labelStyle={glowTooltipLabelStyle(themeRgb('cyan'))}
                                              formatter={(v) => [`${v} lbs`, '']}
                                              labelFormatter={() => ''}
                                            />
                                          </LineChart>
                                        </ResponsiveContainer>
                                      </div>
                                      <span className="text-[9px] text-muted-foreground/60">
                                        {hist.total_logs} {fr ? 'logs' : 'logs'} - PR: <span className="text-amber-400 font-semibold">{hist.max_weight}lbs</span>
                                      </span>
                                    </div>
                                  )}
                                  {/* OPERATION FANTOME — Ghost from previous session */}
                                  {(() => {
                                    const ghost = ghostData[ex.name?.trim().toLowerCase()];
                                    if (!ghost || isEditing) return null;
                                    return (
                                      <div className={`flex items-center gap-2 px-3 py-1.5 bg-violet-500/5 border-t border-violet-500/10 rounded-b-lg`} data-testid={`ghost-${exKey}`}>
                                        <Ghost className="w-3 h-3 text-violet-400/70 shrink-0" />
                                        <span className="text-[10px] text-violet-300/80 font-medium">
                                          {fr ? 'Dernière séance' : 'Last session'}: <span className="text-violet-300 font-bold">{ghost.weight} {ghost.weight_unit || 'lbs'}</span> {fr ? 'pour' : 'for'} <span className="text-violet-300 font-bold">{ghost.reps}</span> reps
                                        </span>
                                      </div>
                                    );
                                  })()}
                                  {/* 🔁 ADAPTIVE LOOP — failure-streak suggestion banner */}
                                  {failureSuggestion?.exKey === exKey && (
                                    <div
                                      className="flex flex-wrap items-center gap-2 px-3 py-2 bg-amber-500/10 border-t border-amber-500/30 rounded-b-lg"
                                      data-testid={`failure-suggestion-${exKey}`}
                                    >
                                      <span className="text-base shrink-0" aria-hidden="true">⚠️</span>
                                      <p className="text-[10.5px] text-amber-200/95 leading-snug flex-1 min-w-[180px]">
                                        {fr
                                          ? <>Échec musculaire répété. Le Coach suggère le mode <span className="font-bold text-amber-100">🐢 Conservateur</span> pour le reste de la semaine afin d&apos;optimiser la récupération.</>
                                          : <>Repeated muscle failure. The Coach suggests <span className="font-bold text-amber-100">🐢 Conservative</span> mode for the rest of the week to optimize recovery.</>}
                                      </p>
                                      <div className="flex items-center gap-1.5 shrink-0">
                                        <button
                                          type="button"
                                          onClick={acceptFailureSuggestion}
                                          className="px-2.5 py-1 rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950 text-[10px] font-bold uppercase tracking-wider active:scale-95 transition-all"
                                          data-testid={`failure-accept-${exKey}`}
                                        >
                                          {fr ? 'Accepter' : 'Accept'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={dismissFailureSuggestion}
                                          className="px-2.5 py-1 rounded-full border border-amber-500/40 text-amber-200/80 hover:bg-amber-500/10 text-[10px] font-bold uppercase tracking-wider active:scale-95 transition-all"
                                          data-testid={`failure-ignore-${exKey}`}
                                        >
                                          {fr ? 'Ignorer' : 'Ignore'}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {/* Iter. 406.fq + 406.fr.2 — Cooldown block with fallback for legacy plans */}
                            {(() => {
                              const { items: cooldownItems, isDefault: cooldownDefault } = resolveCooldown(day, fr);
                              const totalSec = cooldownItems.reduce((s, it) => s + (Number(it.duration_seconds) || 0), 0);
                              const totalLabel = totalSec > 0 ? `~${Math.max(5, Math.round(totalSec / 60))} min` : '~5 min';
                              return (
                                <details
                                  open
                                  className="rounded-lg border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 to-sky-500/5 mt-3 shadow-sm"
                                  data-testid={`cooldown-${wi}-${di}`}
                                >
                                  <summary className="cursor-pointer list-none px-3 py-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cyan-300 hover:bg-cyan-500/15 rounded-lg transition-colors">
                                    <span className="text-base" aria-hidden="true">🧘</span>
                                    <Wind className="w-3.5 h-3.5" />
                                    <span className="truncate">{fr ? 'Retour au calme & Flexibilité' : 'Cool-down & Flexibility'}</span>
                                    <span className="ml-auto text-[10px] font-normal text-cyan-300/80 shrink-0">{totalLabel}</span>
                                    <ChevronDown className="w-3.5 h-3.5 text-cyan-300/60 transition-transform shrink-0" />
                                  </summary>
                                  {cooldownDefault && (
                                    <p className="px-3 pt-1 pb-1 text-[10px] text-cyan-200/70 italic leading-snug" data-testid={`cooldown-default-note-${wi}-${di}`}>
                                      {fr
                                        ? 'Modèle standard — régénère ton plan pour un retour au calme personnalisé par l\'IA.'
                                        : 'Default template — regenerate your plan for a fully AI-personalized cool-down.'}
                                    </p>
                                  )}
                                  <ul className="px-3 pb-3 pt-1 space-y-2">
                                    {cooldownItems.map((c, cIdx) => {
                                      const kind = c.kind || 'stretch';
                                      const KindIcon = kind === 'breath' ? Heart : kind === 'cardio' ? Activity : Leaf;
                                      const durLabel = formatDurationLabel(Number(c.duration_seconds));
                                      return (
                                        <li
                                          key={cIdx}
                                          className="flex items-start gap-2.5 text-xs text-foreground/95"
                                          data-testid={`cooldown-item-${wi}-${di}-${cIdx}`}
                                        >
                                          <span className="w-7 h-7 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center shrink-0 mt-0.5">
                                            <KindIcon className="w-3.5 h-3.5 text-cyan-300" />
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-1.5 flex-wrap">
                                              <span className="font-semibold leading-snug">{c.name || '—'}</span>
                                              {durLabel && (
                                                <span className="text-[10px] font-bold text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded-full">
                                                  {durLabel}
                                                </span>
                                              )}
                                            </div>
                                            {c.notes && (
                                              <p className="text-[10.5px] text-muted-foreground/85 mt-0.5 italic leading-snug">{c.notes}</p>
                                            )}
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </details>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
        {/* OPERATION SIGNAL DE FIN — Terminer l'entraînement */}
        {!todayCompleted ? (
          <Card className="border-primary/30 bg-primary/5 mt-4" data-testid="complete-workout-card">
            <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="font-heading text-base font-bold uppercase tracking-tight text-foreground flex items-center gap-2">
                  <Flag className="w-4 h-4 text-primary" />
                  {fr ? 'Signal de fin' : 'End Signal'}
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {fr ? 'Tu as termine ta séance ? Valide pour débloquer tes récompenses.' : 'Done with your session? Validate to unlock rewards.'}
                </p>
                {/* Live today's debrief — appears when user has logged something */}
                {weeklyStats && weeklyStats.today_total_exercises > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="today-debrief">
                    <span className="inline-flex items-center gap-1 text-foreground">
                      <Dumbbell className="w-3 h-3 text-primary/70" />
                      <span className="tabular-nums font-semibold">{weeklyStats.today_total_exercises}</span>
                      <span className="text-muted-foreground">{fr ? 'exercices' : 'exercises'}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-foreground">
                      <span className="tabular-nums font-semibold">{weeklyStats.today_total_sets}</span>
                      <span className="text-muted-foreground">{fr ? 'series' : 'sets'}</span>
                    </span>
                    {weeklyStats.today_total_volume_lbs > 0 && (
                      <span className="inline-flex items-center gap-1 text-foreground">
                        <TrendingUp className="w-3 h-3 text-primary/70" />
                        <span className="tabular-nums font-semibold">
                          {Math.round(weeklyStats.today_total_volume_lbs).toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">lbs</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Button
                onClick={handleCompleteWorkout}
                disabled={completing}
                className="w-full sm:w-auto h-12 font-heading text-sm uppercase tracking-wider px-8 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="complete-workout-btn"
              >
                {completing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Flag className="w-4 h-4 mr-2" />}
                {fr ? 'Terminer l\'entraînement' : 'Complete Workout'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-green-500/20 bg-green-500/5 mt-4" data-testid="workout-completed-card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                <Check className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-sm font-medium text-green-400 flex-1">
                {fr ? 'Entraînement du jour termine !' : 'Today\'s workout completed!'}
              </p>
              <StreakShareButton variant="compact" />
            </CardContent>
          </Card>
        )}
        </>
      )}
      {/* end viewMode === 'main' */}
      </>
      )}

      {/* OPERATION DEBRIEFING TACTIQUE — Debrief Form (before Dopamine) */}
      {showDebrief && (
        <WorkoutDebriefForm
          language={language}
          onSubmit={handleDebriefSubmit}
        />
      )}

      {/* OPERATION DOPAMINE — Completion Modal */}
      {completionData && (
        <WorkoutCompletionModal
          data={completionData}
          language={language}
          onClose={() => setCompletionData(null)}
        />
      )}

      {/* OPERATION VITRINE SOCIALE — Streak Share Popup */}
      {showStreakShare && (
        <StreakShareModal
          streak={streakData.streak}
          gradeId={streakData.gradeId}
          userName={user?.warrior_name || user?.name || ''}
          language={language}
          onClose={() => setShowStreakShare(false)}
        />
      )}

      {/* DEFI PERSONNEL — PR Victory Animation */}
      {prVictory && (
        <PRVictoryModal
          data={prVictory}
          language={language}
          onClose={() => setPrVictory(null)}
        />
      )}

      {/* PAYWALL UPSELL removed — clinical adaptation is free for the public beta.
          Protocol toggle only shows when protocol exists, so no Elite gate needed. */}

      {/* RIR EDUCATION MODAL — explains the YUMIX 0..4 RIR scale (reservoir analogy) */}
      {showRirInfo && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowRirInfo(false)}
          data-testid="rir-info-modal"
        >
          <div
            className="bg-card border border-border/60 rounded-2xl max-w-md w-full p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-2 mb-3">
              <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <Info className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-lg uppercase tracking-tight text-foreground">
                  RIR — Reps in Reserve
                </h2>
                <p className="text-[11px] text-muted-foreground italic">
                  {fr ? 'L\'analogie du reservoir' : 'The reservoir analogy'}
                </p>
              </div>
              <button
                onClick={() => setShowRirInfo(false)}
                className="text-muted-foreground hover:text-foreground p-1"
                data-testid="close-rir-info"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              {fr
                ? <>Oublie les mathematiques. Le RIR, c&apos;est simplement :<br/><em className="text-foreground">« Si ta vie en dependait, combien de repetitions PARFAITES aurais-tu pu faire de plus avant que le muscle ne lache completement ? »</em></>
                : <>Forget the math. RIR simply asks:<br/><em className="text-foreground">&ldquo;If your life depended on it, how many MORE perfect reps could you have done before the muscle gave out completely?&rdquo;</em></>}
            </p>

            <div className="space-y-2">
              <div className="flex items-start gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <span className="text-sm font-black text-red-400 shrink-0 w-6 text-center">0</span>
                <span className="text-[11px] text-foreground leading-snug">
                  <strong className="text-red-400">{fr ? 'Echec total.' : 'Total failure.'}</strong>{' '}
                  {fr ? "Le reservoir est vide. La barre t'ecrase ou ne monte plus. Tu as tout donne." : "Tank is empty. The bar crushes you or won't move. You gave it everything."}
                </span>
              </div>
              <div className="flex items-start gap-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <span className="text-sm font-black text-orange-400 shrink-0 w-6 text-center">1</span>
                <span className="text-[11px] text-foreground leading-snug">
                  <strong className="text-orange-400">{fr ? 'Limite absolue.' : 'Absolute limit.'}</strong>{' '}
                  {fr ? "La lumiere rouge clignote. Tu aurais pu forcer une toute derniere rep avec une bonne technique." : "Red light flashing. You could've forced one last rep with good form."}
                </span>
              </div>
              <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <span className="text-sm font-black text-amber-400 shrink-0 w-6 text-center">2</span>
                <span className="text-[11px] text-foreground leading-snug">
                  <strong className="text-amber-400">{fr ? 'Lourd.' : 'Hard.'}</strong>{' '}
                  {fr ? "C'etait lourd, mais tu pouvais en faire deux autres avant l'echec." : "It was heavy, but you could've done two more before failure."}
                </span>
              </div>
              <div className="flex items-start gap-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <span className="text-sm font-black text-emerald-400 shrink-0 w-6 text-center">3</span>
                <span className="text-[11px] text-foreground leading-snug">
                  <strong className="text-emerald-400">{fr ? 'Modere.' : 'Moderate.'}</strong>{' '}
                  {fr ? "Il restait du jus. Trois reps en reserve avant l'echec." : "Gas left in the tank. Three reps in reserve before failure."}
                </span>
              </div>
              <div className="flex items-start gap-2 p-2 rounded-lg bg-sky-500/10 border border-sky-500/20">
                <span className="text-sm font-black text-sky-400 shrink-0 w-6 text-center">4</span>
                <span className="text-[11px] text-foreground leading-snug">
                  <strong className="text-sky-400">{fr ? 'Echauffement / Leger.' : 'Warm-up / Light.'}</strong>{' '}
                  {fr ? "C'etait un echauffement ou une serie de preparation. Il te restait beaucoup de jus." : "Warm-up or prep set. Plenty left in the tank."}
                </span>
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground/60 mt-4 text-center italic">
              {fr ? 'Le Coach YUMIX utilise le RIR pour ajuster ta charge automatiquement.' : 'The Coach YUMIX uses RIR to auto-adjust your loads.'}
            </p>
          </div>
        </div>
      )}

      {/* Progression Aggressiveness mini-coach — surfaces after 3 consecutive rejections */}
      <ProgressionAggressivenessPrompt language={fr ? 'fr' : 'en'} />

      {/* REST TIMER — opens when user taps the rest pill on an exercise row */}
      {planRestSeconds !== null && planRestSeconds > 0 && (
        <RestTimer
          restSeconds={planRestSeconds}
          language={language}
          ghost={pendingAdvanceKey && (() => {
            const [, , ei2] = pendingAdvanceKey.split('-').map(Number);
            const [wi2, di2] = pendingAdvanceKey.split('-').map(Number);
            const exName = plan?.weeks?.[wi2]?.days?.[di2]?.exercises?.[ei2]?.name;
            return exName ? ghostData[exName.trim().toLowerCase()] : null;
          })()}
          onComplete={() => {
            // Auto-advance the set counter for the exercise that triggered the timer.
            // OPT-IN: only exercises whose Timer button was clicked.
            if (pendingAdvanceKey) {
              const [wi, di, ei] = pendingAdvanceKey.split('-').map(Number);
              const ex = plan?.weeks?.[wi]?.days?.[di]?.exercises?.[ei];
              const totalSets = parseInt(ex?.sets, 10) || 0;
              if (totalSets > 0 && ex) advanceSet(pendingAdvanceKey, totalSets, ex);
            }
          }}
          onDismiss={() => { setPlanRestSeconds(null); setPendingAdvanceKey(null); }}
        />
      )}

      {/* EXERCISE COMPLETE MODAL — shown when all sets of an exercise are done */}
      {celebrationEx && (() => {
        const { exKey, ex } = celebrationEx;
        const ghost = ghostData[ex.name?.trim().toLowerCase()];
        const hist = exHistory[ex.name?.trim().toLowerCase()];
        return (
          <ExerciseCompleteModal
            key={exKey}
            language={language}
            exerciseName={ex.name}
            sets={ex.sets}
            reps={ex.reps}
            weight={ex.weight}
            ghost={ghost}
            historyMax={hist?.max_weight || 0}
            tone={user?.communication_style || 'commando'}
            onClose={() => setCelebrationEx(null)}
          />
        );
      })()}

      {/* Iter. 406.fr — REMPLACER L'EXERCICE (Swap Modal) */}
      <SwapSuggestionModal
        swapState={swapState}
        fr={fr}
        setSwapState={setSwapState}
        applySwap={applySwap}
        regenerateSwap={regenerateSwap}
        closeSwap={closeSwap}
      />

    </div>
  );
}
